import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { getCompanyTypeColor, getCompanyTypeTextColor } from '../../../constants/companyTypes';
import ClusterSelector from '../../common/ClusterSelector';
import { CLUSTER_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';
import CountrySelector from '../../common/CountrySelector';
import CompanyTypeSelector from '../../common/CompanyTypeSelector';

const CompaniesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedClusters, setSelectedClusters] = useState([]);
  
  const [companyModal, setCompanyModal] = useState({
    open: false,
    isEdit: false,
    formData: {
      name: '',
      countryId: '',
      clusterId: '',
      companyType: ''
    },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, company: null });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getCompanies();
      setCompanies(data);
    } catch (err) {
      showAlert('Failed to load companies.', 'error', err);
      console.error('Error fetching companies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await organizationApi.getCountries();
      setCountries(data);
    } catch (err) {
      showAlert('Failed to load countries.', 'error', err);
      console.error('Error fetching countries:', err);
    }
  };

  useEffect(() => {
    Promise.all([fetchCompanies(), fetchCountries()]);
  }, []);

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (companyModal.isEdit) {
        if (!companyModal.originalId) {
          showAlert('Invalid company data for update', 'error');
          return;
        }
        
        await organizationApi.updateCompany(companyModal.originalId, {
          name: companyModal.formData.name,
          countryId: companyModal.formData.countryId,
          clusterId: companyModal.formData.clusterId || null,
          companyType: companyModal.formData.companyType || null
        });
        showAlert('Company updated successfully', 'success');
      } else {
        await organizationApi.createCompany({
          name: companyModal.formData.name,
          countryId: companyModal.formData.countryId,
          clusterId: companyModal.formData.clusterId || null,
          companyType: companyModal.formData.companyType || null
        });
        showAlert('Company added successfully', 'success');
      }
      
      await fetchCompanies();
      setCompanyModal({
        open: false,
        isEdit: false,
        formData: {
          name: '',
          countryId: '',
          clusterId: '',
          companyType: ''
        },
        originalId: null
      });
    } catch (err) {
      const action = companyModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} company.`, 'error', err);
      console.error(`Error ${action}ing company:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompany = (company) => {
    setDeleteModal({ open: true, company });
  };

  const confirmDelete = async () => {
    const companyToDelete = deleteModal.company;
    setDeleteModal({ open: false, company: null });
    
    if (!companyToDelete?.id) {
      showAlert('Invalid company data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteCompany(companyToDelete.id);
      await fetchCompanies();
      showAlert('Company deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting company:', err);
      showAlert(err.message || 'Failed to delete company.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCountries = selectedCountries.length === 0 || (company.country && selectedCountries.includes(company.country.id));
    const matchesClusters = selectedClusters.length === 0 || (company.cluster && selectedClusters.includes(company.cluster.id));
    return matchesSearch && matchesCountries && matchesClusters;
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Companies Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search Company"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
                flex: 1,
              }}
            />

            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="countries-filter-label">Filter by Countries</InputLabel>
              <Select
                labelId="countries-filter-label"
                multiple
                value={selectedCountries}
                onChange={(e) => setSelectedCountries(e.target.value)}
                input={<OutlinedInput label="Filter by Countries" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={countries.find(c => c.id === value)?.name} 
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                size="small"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ADR-121: a company belongs to a Region, so the filter offers the
                same set as the Individuals Repository — Clusters of the Region
                structure type, flat. */}
            <Box sx={{ minWidth: 200, flex: 1 }}>
              <ClusterSelector
                multiple
                value={selectedClusters}
                onChange={setSelectedClusters}
                label="Filter by Regions"
                placeholder="Select regions..."
                structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
                returnValue={CLUSTER_RETURN_TYPES.ID}
                size="small"
              />
            </Box>

            <HeaderButton
              onClick={() => setCompanyModal({
                open: true,
                isEdit: false,
                formData: {
                  name: '',
                  countryId: '',
                  clusterId: '',
                  companyType: ''
                },
                originalId: null
              })}
              style={{ whiteSpace: 'nowrap' }}
            >
              <FaPlus /> Add Company
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredCompanies.map((company) => (
                  <Grid 
                    item
                    key={company.id}
                  >
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '140px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '8px'
                            }}
                          >
                            {company.name}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6c757d',
                                fontSize: '12px'
                              }}
                            >
                              {company.country?.name || 'No Country'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6c757d',
                                fontSize: '12px'
                              }}
                            >
                              •
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6c757d',
                                fontSize: '12px'
                              }}
                            >
                              {company.cluster?.name || 'No Region'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6c757d',
                                fontSize: '12px'
                              }}
                            >
                              •
                            </Typography>
                            <Chip
                              label={company.companyType || 'Not Set'}
                              size="small"
                              sx={{
                                backgroundColor: getCompanyTypeColor(company.companyType),
                                color: getCompanyTypeTextColor(company.companyType),
                                fontWeight: 500,
                                fontSize: '11px',
                                height: '20px'
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setCompanyModal({
                              open: true,
                              isEdit: true,
                              formData: {
                                name: company.name,
                                countryId: company.country?.id || '',
                                clusterId: company.cluster?.id || '',
                                companyType: company.companyType || ''
                              },
                              originalId: company.id
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteCompany(company)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      <Modal
        isOpen={companyModal.open}
        onClose={() => setCompanyModal({
          open: false,
          isEdit: false,
          formData: {
            name: '',
            countryId: '',
            clusterId: '',
            companyType: ''
          },
          originalId: null
        })}
        onSubmit={handleSaveCompany}
        title={companyModal.isEdit ? "Edit Company" : "Add New Company"}
        submitText={companyModal.isEdit ? "Update Company" : "Add Company"}
      >
        <Box mb={2}>
          <TextField
            id="company-name"
            name="name"
            label="Company Name"
            required
            value={companyModal.formData.name}
            onChange={(e) => setCompanyModal(prev => ({
              ...prev, 
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter company name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>

        <Box mb={2}>
          <CountrySelector
            label="Country"
            value={companyModal.formData.countryId}
            onChange={(value) => setCompanyModal(prev => ({
              ...prev,
              formData: { ...prev.formData, countryId: value }
            }))}
            required
          />
        </Box>

        <Box mb={2}>
          <ClusterSelector
            label="Region"
            value={companyModal.formData.clusterId}
            onChange={(value) => setCompanyModal(prev => ({
              ...prev,
              formData: { ...prev.formData, clusterId: value }
            }))}
            allowSelectAll={true}
            allOptionLabel="None"
            allOptionValue=""
            /* ADR-121: a company belongs to a Region. A company still attached to
               a non-Region Cluster keeps rendering it, disabled and marked. */
            structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
            outOfScopeSuffix="not a Region"
          />
        </Box>

        <Box mb={2}>
          <CompanyTypeSelector
            value={companyModal.formData.companyType}
            onChange={(value) => setCompanyModal(prev => ({
              ...prev,
              formData: { ...prev.formData, companyType: value }
            }))}
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, company: null })}
        onConfirm={confirmDelete}
        title="Delete Company"
        message={`Are you sure you want to delete ${deleteModal.company?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default CompaniesConfiguration; 